"use client";

// ── ServerContentPaint — SNOWCLUB-LAUNCH-REMAINING-PARITY-001 · Gold content equity ─────────────
//
// THE PAGE'S OWN EXPLANATORY COPY, PUT WHERE THE VISITOR CAN READ IT. See `@/lib/painted-content` for
// the measured gap and for which captured blocks are fit to paint.
//
// SAME MECHANISM AS SiteLinkNavPaint, for the same measured reason: the design is a fixed full-viewport
// sheet that scrolls INSIDE itself, so nothing in document flow is ever visible once it paints. The
// served blocks stay exactly as delivered (that is what a no-JS reader and a raw crawler get); this
// clones the marked ones into ONE <section> placed immediately ABOVE the design's own footer, in the
// element the design actually scrolls. It restyles nothing the design owns and moves nothing.
//
// THREE RULES THAT KEEP IT HONEST:
//  · It paints only what the server already served in this document. Same words for a visitor, a
//    rendered crawler and a raw crawler — no crawler-only layer, no visitor-only layer.
//  · It belongs to THIS page. The design switches screens in place; the section is withdrawn the moment
//    the address or the design's own visible <h1> stops being the one it was placed under, and comes
//    back if the visitor returns. Copy about Banff's airport never rides along onto another screen.
//  · Every failure path leaves the page exactly as it was without this component.

import { useEffect } from "react";
import { hasRenderedTemplate, resolveHost } from "./design-restore";
import { designFooter, findScrollContainer, isVisible, waitForRenderedTemplate } from "./SiteLinkNavPaint";

const PAINT_ATTR = "data-sg-content-paint";
const DEBOUNCE_MS = 250;

const SECTION_CSS = [
  "box-sizing:border-box", "width:100%", "max-width:1120px", "margin:0 auto", "padding:56px 24px 64px",
  "font-family:var(--sg-font-web, Montserrat, system-ui, sans-serif)", "color:var(--sg-ink-800, #1d2a33)",
  "font-size:15px", "line-height:1.65", "text-align:left",
].join(";");
const P_CSS = "margin:0 0 12px";
const UL_CSS = "margin:0 0 14px;padding-left:22px;list-style:disc";
const TAG_CSS: Record<string, string> = {
  H2: "margin:36px 0 12px;font-size:clamp(20px,2.4vw,26px);line-height:1.25;font-weight:700;color:var(--sg-ink-900, #101820)",
  H3: "margin:24px 0 8px;font-size:17px;line-height:1.35;font-weight:700;color:var(--sg-ink-900, #101820)",
  H4: "margin:18px 0 6px;font-size:15px;line-height:1.4;font-weight:700;color:var(--sg-ink-900, #101820)",
  P: P_CSS,
  UL: UL_CSS,
  LI: "margin:0 0 6px",
};

const LINK_CSS = "color:var(--sg-color-avalanche-orange, #e8590c);text-decoration:underline";
const tidy = (t: string) => t.replace(/\s+/g, " ").replace(/ ([,.;:!?])/g, "$1");

/** The served block's text AND its internal anchors, cloned in order. Anchors keep their href verbatim
 *  (host-relative contract paths); everything else is copied as text, whitespace normalised. */
function cloneInline(from: Element, into: HTMLElement): void {
  const nodes = Array.from(from.childNodes);
  nodes.forEach((node, i) => {
    let text = tidy(node.textContent ?? "");
    if (i === 0) text = text.replace(/^\s+/, "");
    if (i === nodes.length - 1) text = text.replace(/\s+$/, "");
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const a = el.tagName === "A" ? el : null;
      const inner = a ?? el.querySelector("a[href]");
      if (inner?.getAttribute("href")) {
        if (!a) {
          // a wrapper span around one anchor: keep the wrapper's leading text, then the anchor
          const lead = tidy(Array.from(el.childNodes).filter((n) => n !== inner && n.nodeType === Node.TEXT_NODE && (inner.compareDocumentPosition(n) & Node.DOCUMENT_POSITION_PRECEDING)).map((n) => n.textContent ?? "").join(""));
          if (lead) into.appendChild(document.createTextNode(lead));
        }
        const copy = document.createElement("a");
        copy.setAttribute("href", inner.getAttribute("href") ?? "");
        copy.textContent = tidy(inner.textContent ?? "").trim();
        copy.style.cssText = LINK_CSS;
        into.appendChild(copy);
        if (!a) {
          const tail = tidy(Array.from(el.childNodes).filter((n) => n !== inner && n.nodeType === Node.TEXT_NODE && (inner.compareDocumentPosition(n) & Node.DOCUMENT_POSITION_FOLLOWING)).map((n) => n.textContent ?? "").join(""));
          if (tail) into.appendChild(document.createTextNode(tail));
        }
        return;
      }
    }
    if (text) into.appendChild(document.createTextNode(text));
  });
}

function visibleDesignH1(host: Element): string {
  const h = Array.from(host.querySelectorAll<HTMLElement>("h1")).find(isVisible);
  return (h?.textContent ?? "").replace(/\s+/g, " ").trim();
}

/** One <section> from the served, marked blocks — in served order, list items regrouped into lists. */
function buildSection(label: string): HTMLElement | null {
  // The source blocks now live INSIDE the design surface (ExportContentBody, data-sg-route-content), not in a fallback layer.
  const marked = Array.from(document.querySelectorAll<HTMLElement>("[data-sg-route-content] [data-sg-paint]"));
  if (!marked.length) return null;
  const section = document.createElement("section");
  section.setAttribute(PAINT_ATTR, "");
  section.setAttribute("data-sg-cloned", "1");
  section.setAttribute("aria-label", label);
  section.style.cssText = SECTION_CSS;
  let list: HTMLElement | null = null;
  let tabRow: HTMLElement | null = null;
  let first = true;
  for (const el of marked) {
    // Live's section tabs: one row of ordinary links, the current page named rather than linked.
    if (el.hasAttribute("data-sg-tab")) {
      if (!tabRow) {
        tabRow = document.createElement("nav");
        tabRow.setAttribute("aria-label", "Transport information");
        tabRow.style.cssText = "display:flex;flex-wrap:wrap;gap:8px 20px;margin:0 0 28px;font-weight:600;font-size:14px;letter-spacing:.02em";
        section.appendChild(tabRow);
      }
      const a = el.querySelector("a[href]");
      const item = document.createElement(a ? "a" : "span");
      item.textContent = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (a) { item.setAttribute("href", a.getAttribute("href") ?? ""); item.style.cssText = "color:var(--sg-color-avalanche-orange, #e8590c);text-decoration:underline"; }
      else { item.setAttribute("aria-current", "page"); item.style.cssText = "color:var(--sg-ink-900, #101820)"; }
      tabRow.appendChild(item);
      continue;
    }
    const copy = document.createElement(el.tagName.toLowerCase());
    if (el.querySelector("a[href]")) {
      // SNOWCLUB-LAUNCH-REMAINING-PARITY-001 · ranked-route parity — a served block that carries internal
      // links (the route pages' related-route list and FAQ answers) keeps them as ANCHORS in the clone.
      // Flattening to textContent turned a crawlable, clickable internal link into dead prose on the
      // visitor's surface — the crawler-only-link shape the founder's 2026-08-21 ruling forbids.
      // Text-only blocks (every block the three transport-information families serve) take the
      // unchanged path below, so their clone is byte-identical to before.
      cloneInline(el, copy);
    } else {
      // The served words, with the capture's stray space before punctuation closed ("Banff Sunshine ,").
      copy.textContent = (el.textContent || "").replace(/\s+/g, " ").replace(/ ([,.;:!?])/g, "$1").trim();
    }
    copy.style.cssText = TAG_CSS[el.tagName] ?? P_CSS;
    if (first && /^H[2-4]$/.test(el.tagName)) copy.style.marginTop = "0";
    first = false;
    if (el.tagName === "LI") {
      if (!list) { list = document.createElement("ul"); list.style.cssText = UL_CSS; section.appendChild(list); }
      list.appendChild(copy);
    } else {
      list = null;
      section.appendChild(copy);
    }
  }
  return section;
}

export default function ServerContentPaint({ label }: { label: string }) {
  useEffect(() => {
    let section: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let stopWaiting: (() => void) | null = null;
    let debounce: number | null = null;
    let disposed = false;
    const homePath = window.location.pathname;
    let homeH1 = "";

    const designIsUp = (): boolean => {
      try { return hasRenderedTemplate(resolveHost(null, document)); } catch { return false; }
    };

    /** True when, on return, the section is exactly where it belongs — or correctly withdrawn. */
    const place = (): boolean => {
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (!host || !section) return false;
      const h1 = visibleDesignH1(host);
      if (!homeH1) homeH1 = h1;
      // Not this page any more (in-place screen switch): withdraw, keep the node for a return.
      if (window.location.pathname !== homePath || (h1 && homeH1 && h1 !== homeH1)) {
        if (section.isConnected) section.remove();
        return true;
      }
      const footer = designFooter(host);
      const footerParent = footer?.parentElement;
      if (footer && footerParent && footerParent !== host) {
        if (footer.previousElementSibling !== section) footerParent.insertBefore(section, footer);
        return footer.previousElementSibling === section;
      }
      const container = findScrollContainer(host);
      if (!container) return false;
      if (section.parentElement !== container) container.appendChild(section);
      return section.parentElement === container;
    };

    const start = (): boolean => {
      if (disposed) return false;
      section ??= buildSection(label);
      if (!section || !place()) return false;
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (!host) return false;
      for (const el of Array.from(document.querySelectorAll<HTMLElement>(`[${PAINT_ATTR}]`))) if (el !== section) el.remove();
      // The served route-content block has been re-homed into the design above its footer: REMOVE the source (not hide it),
      // so the visitor's page holds one copy and the no-JS document (which never runs this) keeps it in place.
      for (const src of Array.from(document.querySelectorAll<HTMLElement>("[data-sg-route-content]"))) src.remove();
      observer = new MutationObserver(() => {
        if (disposed || debounce !== null) return;
        debounce = window.setTimeout(() => {
          debounce = null;
          if (disposed) return;
          try { place(); } catch { /* the previous placement stands */ }
        }, DEBOUNCE_MS);
      });
      observer.observe(host, { childList: true, subtree: true });
      return true;
    };

    try {
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (host) stopWaiting = waitForRenderedTemplate(host, designIsUp, () => !disposed && designIsUp() && start());
    } catch { /* the page stays exactly as served */ }

    return () => {
      disposed = true;
      try { section?.remove(); } catch { /* already detached */ }
      section = null;
      try { observer?.disconnect(); } catch { /* already disconnected */ }
      try { stopWaiting?.(); } catch { /* already disconnected */ }
      if (debounce !== null) window.clearTimeout(debounce);
    };
  }, [label]);

  return null;
}

/** True once the design is up AND (when `requires` is named) its own copy of the words is painted. */
export function designOwnsCopy(doc: Document, host: Element, requires?: string, visible: (el: Element) => boolean = isVisible): boolean {
  try {
    if (!hasRenderedTemplate(resolveHost(null, doc))) return false;
    if (!requires) return true;
    return Array.from(host.querySelectorAll(requires)).some(visible);
  } catch { return false; }
}

/** Removes the served route-content blocks; returns how many. `:not(main)` — a family view also
 *  stamps data-sg-route-content on <main> itself, and that must never go. */
export function withdrawServedRouteContent(doc: Document): number {
  const served = Array.from(doc.querySelectorAll<HTMLElement>("[data-sg-route-content]:not(main)"));
  for (const src of served) src.remove();
  return served.length;
}

/** For a page whose design paints its OWN copy of the served words (Whistler's General Information
 *  article): nothing is cloned, but the served [data-sg-route-content] block is still withdrawn once
 *  the design is up — otherwise the visitor scrolls past the designed page into a second,
 *  legacy-styled copy of it. `requires` names the design's own element that must be painted first;
 *  until it is (or if it never is) the served block stays exactly as delivered. The no-JS document
 *  never runs this, so a reader without JS keeps the served copy in place. */
export function ServerContentWithdraw({ requires }: { requires?: string }) {
  useEffect(() => {
    let stopWaiting: (() => void) | null = null;
    let disposed = false;
    const homePath = window.location.pathname;
    try {
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (host) stopWaiting = waitForRenderedTemplate(host, () => designOwnsCopy(document, host, requires), () => {
        if (disposed || window.location.pathname !== homePath || !designOwnsCopy(document, host, requires)) return false;
        withdrawServedRouteContent(document);
        return true;
      });
    } catch { /* the page stays exactly as served */ }
    return () => {
      disposed = true;
      try { stopWaiting?.(); } catch { /* already disconnected */ }
    };
  }, [requires]);

  return null;
}
